import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../services/api';
import {
  TEACHER_BATCHES,
  TEACHERS_STUDENTS,
  TESTS,
  SUBJECTS,
  SUBJECT_TOPICS,
  BOARDS_EXAMS,
  ACADEMIC_YEARS,
  ASSIGNMENTS,
  ATTEMPTS,
} from '../../config/Config';
import { filterPublished } from '../../utils/publishedFilter';
import {
  aggregateBatchStats,
  computeStudentSnapshot,
  fetchAllTeacherStudentRelationships,
  normalizeTeacherStudents,
} from '../../components/teacher/batch/batchDetailUtils';
import BatchDetailHeader from '../../components/teacher/batch/BatchDetailHeader';
import BatchStatsSummary from '../../components/teacher/batch/BatchStatsSummary';
import BatchActionBar from '../../components/teacher/batch/BatchActionBar';
import BatchEnrollmentCard from '../../components/teacher/batch/BatchEnrollmentCard';
import BatchStudentRoster from '../../components/teacher/batch/BatchStudentRoster';

export default function TeacherBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [studentsPool, setStudentsPool] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [statsByStudentId, setStatsByStudentId] = useState({});
  const [toAdd, setToAdd] = useState([]);
  const [adding, setAdding] = useState(false);

  const [assignTestOpen, setAssignTestOpen] = useState(false);
  const [tests, setTests] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [targetDate, setTargetDate] = useState('');
  const [assignTestBusy, setAssignTestBusy] = useState(false);
  const [assignTestResult, setAssignTestResult] = useState(null);

  const [planOpen, setPlanOpen] = useState(false);
  const [planBusy, setPlanBusy] = useState(false);
  const [planResult, setPlanResult] = useState(null);
  const [boards, setBoards] = useState([]);
  const [years, setYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [planForm, setPlanForm] = useState({
    exam_name: '',
    exam_date: '',
    subject: '',
    topic_ids: [],
    board: '',
    academic_year: '',
    mode: 'balanced',
    percent_basic: 50,
    percent_advanced: 50,
  });

  const loadBatch = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`${TEACHER_BATCHES}${batchId}/`);
      setBatch(res.data);
    } catch (e) {
      setError('Could not load this batch.');
      setBatch(null);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    loadBatch();
  }, [loadBatch]);

  useEffect(() => {
    const members = batch?.members || [];
    if (!members.length) {
      setStatsByStudentId({});
      return;
    }
    let cancelled = false;
    const initial = {};
    members.forEach((m) => {
      initial[m.student] = { loading: true, avgScore: null, timelyPct: null };
    });
    setStatsByStudentId(initial);
    (async () => {
      const rows = await Promise.all(
        members.map(async (m) => {
          const sid = m.student;
          try {
            const asRes = await api.get(`${ASSIGNMENTS}?student=${sid}&page_size=1000`);
            let attRes;
            try {
              attRes = await api.get(`${ATTEMPTS}?student_id=${sid}&page_size=1000`);
            } catch {
              attRes = await api.get(`${ATTEMPTS}?student=${sid}&page_size=1000`);
            }
            const assigns = Array.isArray(asRes.data) ? asRes.data : asRes.data?.results || [];
            const attempts = Array.isArray(attRes.data) ? attRes.data : attRes.data?.results || [];
            const snap = computeStudentSnapshot(assigns, attempts);
            return { sid, loading: false, ...snap };
          } catch {
            return { sid, loading: false, avgScore: null, timelyPct: null, error: true };
          }
        })
      );
      if (cancelled) return;
      const next = {};
      rows.forEach((r) => {
        next[r.sid] = { loading: false, avgScore: r.avgScore, timelyPct: r.timelyPct, error: r.error };
      });
      setStatsByStudentId(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [batch?.id, batch?.members]);

  const fetchStudentsPool = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const list = await fetchAllTeacherStudentRelationships(api, TEACHERS_STUDENTS);
      const active = list.filter((s) => s.is_active !== false);
      setStudentsPool(normalizeTeacherStudents(active));
    } catch {
      setStudentsPool([]);
      setError('Could not load your student list for this batch.');
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudentsPool();
  }, [fetchStudentsPool]);

  const memberIds = useMemo(() => new Set((batch?.members || []).map((m) => Number(m.student))), [batch]);

  const addableOptions = useMemo(
    () => studentsPool.filter((s) => s.id && !memberIds.has(Number(s.id))),
    [studentsPool, memberIds]
  );

  const batchStats = useMemo(
    () => aggregateBatchStats(batch?.members || [], statsByStudentId),
    [batch?.members, statsByStudentId]
  );

  const handleAddStudents = async () => {
    if (!batchId || toAdd.length === 0) return;
    setAdding(true);
    try {
      await api.post(`${TEACHER_BATCHES}${batchId}/students/`, {
        student_ids: toAdd.map((s) => Number(s.id)),
      });
      setToAdd([]);
      await loadBatch();
    } catch {
      setError('Failed to add one or more students.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (studentId) => {
    if (!batchId) return;
    try {
      await api.delete(`${TEACHER_BATCHES}${batchId}/students/${studentId}/`);
      await loadBatch();
    } catch {
      setError('Could not remove student from batch.');
    }
  };

  const openAssignTest = async () => {
    setAssignTestOpen(true);
    setAssignTestResult(null);
    setSelectedTest(null);
    setTargetDate('');
    try {
      const res = await api.get(TESTS, { params: { status: 'PUBLISHED', page_size: 200 } });
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setTests(list);
    } catch {
      setTests([]);
    }
  };

  const runAssignTest = async () => {
    if (!batchId || !selectedTest?.id) return;
    setAssignTestBusy(true);
    setAssignTestResult(null);
    try {
      const res = await api.post(`${TEACHER_BATCHES}${batchId}/assign-test/`, {
        test: selectedTest.id,
        target_date: targetDate || null,
      });
      setAssignTestResult(res.data);
      await loadBatch();
    } catch (e) {
      setAssignTestResult({ error: e?.response?.data?.detail || e?.message || 'Request failed' });
    } finally {
      setAssignTestBusy(false);
    }
  };

  const openPlanDialog = async () => {
    setPlanOpen(true);
    setPlanResult(null);
    try {
      const [bRes, yRes, sRes, tRes] = await Promise.all([
        api.get(`${BOARDS_EXAMS}?page_size=100&published_only=true`),
        api.get(`${ACADEMIC_YEARS}?page_size=100&published_only=true`),
        api.get(`${SUBJECTS}?page_size=100`),
        api.get(`${SUBJECT_TOPICS}?page_size=2000`),
      ]);
      setBoards(filterPublished(Array.isArray(bRes.data) ? bRes.data : bRes.data?.results || []));
      setYears(filterPublished(Array.isArray(yRes.data) ? yRes.data : yRes.data?.results || []));
      setSubjects(Array.isArray(sRes.data) ? sRes.data : sRes.data?.results || []);
      setTopics(Array.isArray(tRes.data) ? tRes.data : tRes.data?.results || []);
    } catch {
      setBoards([]);
      setYears([]);
      setSubjects([]);
      setTopics([]);
    }
  };

  const topicsForSubject = useMemo(() => {
    const sid = planForm.subject ? String(planForm.subject) : '';
    if (!sid) return [];
    return topics.filter((t) => {
      const subj = t.subject ?? t.subject_id ?? (t.subject && t.subject.id);
      return String(subj) === sid;
    });
  }, [topics, planForm.subject]);

  const runAssignPlan = async () => {
    if (!batchId) return;
    const pb = Number(planForm.percent_basic);
    const pa = Number(planForm.percent_advanced);
    if (pb + pa !== 100) {
      setPlanResult({ error: 'Basic and advanced percentages must total 100.' });
      return;
    }
    if (!planForm.exam_name?.trim() || !planForm.exam_date || !planForm.subject || planForm.topic_ids.length === 0) {
      setPlanResult({ error: 'Fill exam name, date, subject, at least one topic, board, and year.' });
      return;
    }
    if (!planForm.board || !planForm.academic_year) {
      setPlanResult({ error: 'Board and academic year are required.' });
      return;
    }
    setPlanBusy(true);
    setPlanResult(null);
    try {
      const res = await api.post(`${TEACHER_BATCHES}${batchId}/assign-plan/`, {
        exam_name: planForm.exam_name.trim(),
        exam_date: planForm.exam_date,
        subject: Number(planForm.subject),
        topic_ids: planForm.topic_ids.map((t) => Number(typeof t === 'object' ? t.id : t)),
        board: Number(planForm.board),
        academic_year: Number(planForm.academic_year),
        mode: planForm.mode,
        percent_basic: pb,
        percent_advanced: pa,
        activate: true,
        weekly_availability_hours: {},
        prep_test_my_questions: false,
      });
      setPlanResult(res.data);
    } catch (e) {
      const d = e?.response?.data;
      setPlanResult({ error: typeof d === 'object' ? JSON.stringify(d) : d || e?.message || 'Request failed' });
    } finally {
      setPlanBusy(false);
    }
  };

  const handleArchive = async () => {
    if (!batchId) return;
    const ok = window.confirm(
      'Archive this batch?\n\nStudents stay in your roster. You cannot run new batch assignments until you restore the batch from the batch list (restore action).'
    );
    if (!ok) return;
    try {
      await api.delete(`${TEACHER_BATCHES}${batchId}/`);
      navigate('/teacher/batches');
    } catch {
      setError('Could not archive batch.');
    }
  };

  const handleUnarchive = async () => {
    if (!batchId) return;
    try {
      await api.patch(`${TEACHER_BATCHES}${batchId}/`, { status: 'active' });
      await loadBatch();
    } catch {
      setError('Could not restore batch.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1080, mx: 'auto' }}>
        <Skeleton width={160} height={24} sx={{ mb: 2 }} />
        <Skeleton width="55%" height={44} sx={{ mb: 1 }} />
        <Skeleton width="40%" height={22} sx={{ mb: 3 }} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <Skeleton variant="rounded" height={100} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={100} sx={{ flex: 1, borderRadius: 2 }} />
          <Skeleton variant="rounded" height={100} sx={{ flex: 1, borderRadius: 2 }} />
        </Stack>
        <Skeleton variant="rounded" height={72} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" height={140} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 720 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Button component={Link} to="/teacher/batches" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} variant="outlined">
          Back to batches
        </Button>
      </Box>
    );
  }

  const archived = batch.status === 'archived';
  const members = batch.members || [];
  const memberCount = members.length;

  return (
    <Box
      component="main"
      sx={{
        p: { xs: 2, sm: 3 },
        pb: 5,
        maxWidth: 1080,
        mx: 'auto',
      }}
    >
      <BatchDetailHeader batch={batch} archived={archived} memberCount={memberCount} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <BatchStatsSummary
        studentCount={batchStats.studentCount}
        batchAvgScore={batchStats.batchAvgScore}
        batchTimelyPct={batchStats.batchTimelyPct}
        statsLoading={batchStats.statsLoading}
      />

      <BatchActionBar
        archived={archived}
        onAssignTest={openAssignTest}
        onAssignPlan={openPlanDialog}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
      />

      <BatchEnrollmentCard
        archived={archived}
        adding={adding}
        studentsLoading={studentsLoading}
        addableOptions={addableOptions}
        toAdd={toAdd}
        onToAddChange={setToAdd}
        onAdd={handleAddStudents}
      />

      <BatchStudentRoster members={members} statsByStudentId={statsByStudentId} archived={archived} onRemoveMember={handleRemoveMember} />

      <Dialog open={assignTestOpen} onClose={() => !assignTestBusy && setAssignTestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign test to all students in batch</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={tests}
              getOptionLabel={(t) => `${t.title || 'Test'} (#${t.id})`}
              value={selectedTest}
              onChange={(_, v) => setSelectedTest(v)}
              renderInput={(params) => <TextField {...params} label="Published test" />}
            />
            <TextField
              type="date"
              label="Target date (optional)"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            {assignTestResult?.summary && (
              <Alert severity={assignTestResult.summary.failed ? 'warning' : 'success'}>
                Succeeded: {assignTestResult.summary.succeeded} / {assignTestResult.summary.total}
              </Alert>
            )}
            {assignTestResult?.results && (
              <Box component="ul" sx={{ pl: 2, m: 0, maxHeight: 200, overflow: 'auto' }}>
                {assignTestResult.results.map((r) => (
                  <li key={r.student_id}>
                    <Typography variant="caption">
                      Student {r.student_id}: {r.ok ? 'OK' : r.error || 'failed'}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
            {assignTestResult?.error && <Alert severity="error">{assignTestResult.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTestOpen(false)} disabled={assignTestBusy}>
            Close
          </Button>
          <Button variant="contained" onClick={runAssignTest} disabled={assignTestBusy || !selectedTest}>
            {assignTestBusy ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={planOpen} onClose={() => !planBusy && setPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign exam prep plan to each student</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Exam / goal name"
              value={planForm.exam_name}
              onChange={(e) => setPlanForm((p) => ({ ...p, exam_name: e.target.value }))}
              fullWidth
            />
            <TextField
              type="date"
              label="Exam date"
              value={planForm.exam_date}
              onChange={(e) => setPlanForm((p) => ({ ...p, exam_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              select
              label="Subject"
              value={planForm.subject}
              onChange={(e) => setPlanForm((p) => ({ ...p, subject: e.target.value, topic_ids: [] }))}
              fullWidth
            >
              <MenuItem value="">Select…</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              multiple
              options={topicsForSubject}
              getOptionLabel={(t) => t.topic || t.name || String(t.id)}
              value={planForm.topic_ids}
              onChange={(_, v) => setPlanForm((p) => ({ ...p, topic_ids: v }))}
              disabled={!planForm.subject}
              renderInput={(params) => <TextField {...params} label="Topics" placeholder="Select topics" />}
            />
            <TextField
              select
              label="Board"
              value={planForm.board}
              onChange={(e) => setPlanForm((p) => ({ ...p, board: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">Select…</MenuItem>
              {boards.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name || b.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Academic year"
              value={planForm.academic_year}
              onChange={(e) => setPlanForm((p) => ({ ...p, academic_year: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">Select…</MenuItem>
              {years.map((y) => (
                <MenuItem key={y.id} value={y.id}>
                  {y.label || y.year}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Mode"
              value={planForm.mode}
              onChange={(e) => setPlanForm((p) => ({ ...p, mode: e.target.value }))}
              fullWidth
            >
              <MenuItem value="balanced">Balanced</MenuItem>
              <MenuItem value="weak_focus">Weak focus</MenuItem>
              <MenuItem value="intensive">Intensive</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="% Basic"
                type="number"
                value={planForm.percent_basic}
                onChange={(e) => setPlanForm((p) => ({ ...p, percent_basic: e.target.value }))}
                inputProps={{ min: 0, max: 100 }}
              />
              <TextField
                label="% Advanced"
                type="number"
                value={planForm.percent_advanced}
                onChange={(e) => setPlanForm((p) => ({ ...p, percent_advanced: e.target.value }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Stack>
            {planResult?.summary && (
              <Alert severity={planResult.summary.failed ? 'warning' : 'success'}>
                Plans created: {planResult.summary.succeeded} / {planResult.summary.total}
              </Alert>
            )}
            {planResult?.error && <Alert severity="error">{planResult.error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanOpen(false)} disabled={planBusy}>
            Close
          </Button>
          <Button variant="contained" onClick={runAssignPlan} disabled={planBusy}>
            {planBusy ? 'Creating…' : 'Assign plan to all'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
